import { z } from "zod";
import { DailyFormIssuePriority } from "generated/graphql";
import dayjs from "dayjs";
import { workOrderStatusOptions } from "modules/fleet/workOrderStatusOptions";

const workOrderStatusValues = workOrderStatusOptions.map((option) => option.value) as [
  string,
  ...string[],
];

const fileSchema = z.object({
  name: z.string(),
  path: z.string(),
});

export const updateSchema = z.object({
  id: z.string(),
  issueName: z.string().min(1, "Issue name is required"),
  issueDescription: z.string().optional(),
  issueType: z.string().optional(),
  comments: z.string().optional(),
  priority: z.nativeEnum(DailyFormIssuePriority),
  status: z.enum(workOrderStatusValues),
  neededCompletionDate: z.string().nullable().optional(),
  currentMeterReading: z.number().min(0, "Current meter must be 0 or greater").nullable().optional(),
  estimatedTaskTime: z.number().min(0, "Estimated task time must be 0 or greater").nullable().optional(),
  vehicleId: z.string().optional(),
  assignedTo: z.string().nullable().optional(),
  servicePlanId: z.string().nullable().optional(),
  files: z.array(fileSchema).optional(),
  removeFiles: z.array(z.string()).optional(),
});

export const createSchema = z.object({
  issueName: z.string().min(1, "Issue name is required"),
  issueDescription: z.string().min(1, "Issue description is required"),
  issueType: z.string().min(1, "Issue type is required"),
  comments: z.string().optional(),
  priority: z.nativeEnum(DailyFormIssuePriority),
  status: z.enum(workOrderStatusValues),
  neededCompletionDate: z.string().nullable().optional(),
  currentMeterReading: z.number().min(0, "Current meter must be 0 or greater").nullable().optional(),
  estimatedTaskTime: z.number().min(0, "Estimated task time must be 0 or greater").nullable().optional(),
  vehicleId: z.string().min(1, "Vehicle is required"),
  assignedTo: z.string().nullable().optional(),
  servicePlanId: z.string().nullable().optional(),
  files: z.array(fileSchema).optional(),
});

export type UpdateWorkOrderInput = z.infer<typeof updateSchema>;
export type CreateWorkOrderInput = z.infer<typeof createSchema>;

export const getDefaultValues = (workOrder?: any): UpdateWorkOrderInput | CreateWorkOrderInput => {
  return {
    ...(workOrder?.id && { id: workOrder.id }),
    issueName: workOrder?.issueName || "",
    issueDescription: workOrder?.issueDescription || "",
    issueType: workOrder?.issueType || "",
    comments: workOrder?.comments || "",
    priority: workOrder?.priority || DailyFormIssuePriority.MEDIUM,
    status: workOrder?.status || "OPEN",
    neededCompletionDate: workOrder?.neededCompletionDate
      ? dayjs(workOrder.neededCompletionDate).format("YYYY-MM-DD")
      : null,
    currentMeterReading: workOrder?.currentMeterReading ?? null,
    estimatedTaskTime: workOrder?.estimatedTaskTime ?? null,
    vehicleId: workOrder?.vehicle?.id || "",
    assignedTo: workOrder?.assignedTo?.id || null,
    servicePlanId: workOrder?.servicePlan?.id || null,
  };
};
